<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import TgTooltip from "@/abc-dsk/components/Tooltip/TgTooltip.vue";
import VoiceSearchLogService from "@/common/api//voice-search-log.service";
import IconMic from "@/components/icons/white/IconWhiteMic.vue";
import { MatomoHelper } from "@/helpers";
import { useAuthStore, useFeatureFlagsStore, useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";
import type { VoiceSearchResult } from "@/types/voice-search.type";

const props = defineProps<{
  voiceLanguage: string;
}>();

const emit = defineEmits<{
  (event: "final", transcript: string): void;
}>();

const { t } = useI18n();
const { voiceSearch: allowVoiceSearch } = storeToRefs(useFeatureFlagsStore());

const authStore = useAuthStore();
const settingsStore = useSettingsStore();

const SpeechRecognitionEngine =
  window.SpeechRecognition || window.webkitSpeechRecognition || undefined;

const speechRecognitionInstance = ref<SpeechRecognition | undefined>();

const isListening = ref<boolean>(false);

const isSpeechRecognitionAvailable = computed<boolean>(
  () => speechRecognitionInstance.value !== undefined
);

const isDisabled = computed<boolean>(() => {
  if (!isSpeechRecognitionAvailable.value) {
    return true;
  }

  if (!allowVoiceSearch.value) {
    return true;
  }

  return false;
});

const tooltip = computed<string>(() => {
  if (!isSpeechRecognitionAvailable.value) {
    return t("voiceSearch.webkitRequired");
  }

  if (!allowVoiceSearch.value) {
    return t("voiceSearch.disabled");
  }

  return t("voiceSearch.searchWithVoice");
});

const toggleListening = () => {
  if (isListening.value) {
    return speechRecognitionInstance.value?.stop();
  }

  return speechRecognitionInstance.value?.start();
};

onBeforeMount(() => {
  if (SpeechRecognitionEngine === undefined) {
    return;
  }

  // Configure instance
  speechRecognitionInstance.value = new SpeechRecognitionEngine();
  speechRecognitionInstance.value.interimResults = true;
  speechRecognitionInstance.value.lang = props.voiceLanguage;
  speechRecognitionInstance.value.maxAlternatives = 10;

  // Register event listeners
  speechRecognitionInstance.value.onstart = () => {
    isListening.value = true;
  };

  speechRecognitionInstance.value.onend = () => {
    isListening.value = false;
  };

  speechRecognitionInstance.value.onresult = (event: SpeechRecognitionEvent) => {
    if (!event.results[0]) {
      return;
    }

    if (!event.results[0].isFinal) {
      return;
    }

    logSpeechRecognitionResult(event.results);

    emit("final", event.results[0][0].transcript);
  };
});

const logSpeechRecognitionResult = (list: SpeechRecognitionResultList) => {
  if (!settingsStore.activeLanguageGroup || list[0].length === 0) {
    return;
  }

  const voiceSearchResult: VoiceSearchResult[] = [];

  // Unable to iterate over SpeechRecognitionResultList directly, so we use the good old for loop
  for (let i = 0; i < list[0].length; i++) {
    const result = list[0][i];

    if (!result) {
      break;
    }

    voiceSearchResult.push({
      transcript: result.transcript,
      confidence: result.confidence
    });
  }

  VoiceSearchLogService.log(
    authStore.user.className,
    settingsStore.activeLanguageGroup,
    voiceSearchResult
  );
};

watch(
  () => props.voiceLanguage,
  (lang) => {
    if (!speechRecognitionInstance.value) {
      return;
    }

    speechRecognitionInstance.value.lang = lang;
  }
);

const trackRecordVoiceIconClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ClickSearchbarIcons,
    "Clicked record voice icon"
  );
};
</script>

<template>
  <TgTooltip class="voice-search" :margin-bottom="10" :text="tooltip">
    <button
      type="button"
      class="voice-search__button"
      :disabled="isDisabled"
      :aria-label="t('voiceSearch.searchWithVoice')"
      @click="toggleListening(), trackRecordVoiceIconClick()"
    >
      <IconMic
        v-if="!isListening"
        class="voice-search__icon-button"
        :class="{ 'voice-search__icon-button--disabled': isDisabled }"
        role="presentation"
      />
      <span v-else class="voice-search__pulse-container">
        <span class="voice-search__recording-indicator"></span>
      </span>
    </button>
  </TgTooltip>
</template>

<style lang="less" scoped>
.voice-search {
  &__button {
    border: unset;
    background: unset;
    color: unset;
  }

  &__icon-button {
    &--disabled {
      color: #f0f0f0;
      cursor: default;
    }
  }

  &__pulse-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  &__recording-indicator {
    width: 16px;
    height: 16px;
    transform: scale(1);
    animation: pulse 1.2s infinite;
    border-radius: 50%;
    background: #c20d19;
    box-shadow: 0 0 0 0 #c20d19;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(#c20d19, 0.7);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px rgba(#c20d19, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(#c20d19, 0);
    }
  }
}
</style>
